import config from '@/config'
import { loginByCode } from '@/api/auth'
import { authState, clearAuth, setLoginResult } from '@/store/auth'

const SESSION_KEY = 'jxab:oauth_' + config.wx.appId
const STATE_KEY = 'jxab:oauth_state_' + config.wx.appId
const RETRY_KEY = 'jxab:oauth_retry_' + config.wx.appId
const AUTHORIZE_URL = 'https://open.weixin.qq.com/connect/oauth2/authorize'

function readSession() {
  try {
    return JSON.parse(window.sessionStorage.getItem(SESSION_KEY)) || {}
  } catch {
    return {}
  }
}

function saveSession(session) {
  try {
    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(session))
  } catch {
    throw new Error('请允许微信浏览器保存会话后重试')
  }
}

// iOS 微信按首次进入页面的 URL 计算 JS-SDK 签名，授权回跳后地址栏会被清理，
// 所以在清理前记住入口地址，供 sdk.js 取签名 URL。
function captureEntryUrl() {
  if (window.__ENTRY_URL__) return
  window.__ENTRY_URL__ = window.location.href.split('#')[0]
}

// 回跳地址里的 code 只能消费一次，处理或放弃后都必须从地址栏清掉，避免刷新重放。
function clearOAuthQuery() {
  const url = new URL(window.location.href)
  url.searchParams.delete('code')
  url.searchParams.delete('state')
  if (['#wechat_redirect', '#/wechat_redirect'].includes(url.hash)) url.hash = ''
  if (url.href !== window.location.href) window.history.replaceState(window.history.state, '', url.href)
}

function createState() {
  const state = Math.random().toString(36).slice(2) + Date.now().toString(36)
  try {
    window.sessionStorage.setItem(STATE_KEY, state)
  } catch {
    // 存不下时回跳必然校验失败，只能重新发起授权，不在这里降级放行。
  }
  return state
}

// state 一次性消费；比对失败的回跳 code 不提交，防止他人构造的授权链接被兑换。
function consumeState(value) {
  let saved = ''
  try {
    saved = window.sessionStorage.getItem(STATE_KEY) || ''
    window.sessionStorage.removeItem(STATE_KEY)
  } catch {
    return false
  }
  return Boolean(saved) && saved === value
}

function clearReauthorizeFlag() {
  try {
    window.sessionStorage.removeItem(RETRY_KEY)
  } catch {
    // 存储不可用时本来也无标记可清。
  }
}

function redirectToAuthorize() {
  // 授权地址不能带上残留的旧 code，否则微信回跳会出现重复 code 参数。
  clearOAuthQuery()
  const state = createState()
  // redirect_uri 去掉 hash：微信只会往 query 追加 code/state，深层路由登录成功后再由路由接管。
  const query = new URLSearchParams({
    appid: config.wx.appId,
    redirect_uri: window.location.href.split('#')[0],
    response_type: 'code',
    scope: config.wx.oauth.scope,
    state,
  })
  // 用 replace 跳转，避免用户从授权页返回后停在一个已失效的中间页。
  window.location.replace(AUTHORIZE_URL + '?' + query.toString() + '#wechat_redirect')
}

async function exchangeCode(code, session = readSession()) {
  if (session.lastCode === code) {
    if (authState.token || authState.openid) return
    throw new Error('该微信授权已处理，请重新获取 code')
  }
  clearAuth()
  // code 只能提交一次；失败后也不重放，用户需重新获取 code。
  saveSession({ lastCode: code })
  setLoginResult(await loginByCode(code))
  clearReauthorizeFlag()
}

// 兑换回跳 code：先清地址栏再校验 state，失败只记录错误，由页面决定重授权还是换 code。
async function exchangeCallback(code, state) {
  clearOAuthQuery()
  if (!consumeState(state)) {
    clearAuth()
    authState.error = '微信授权校验失败，请重新授权'
    return
  }
  try {
    await exchangeCode(code)
  } catch (err) {
    authState.error = err.message
  }
}

// 启动认证入口：有 token 放行；有回跳 code 就兑换；否则按构建环境跳微信或显示手动输入。
export async function ensureParentLogin() {
  captureEntryUrl()
  if (authState.token) {
    clearReauthorizeFlag()
    clearOAuthQuery()
    return
  }

  const url = new URL(window.location.href)
  const code = url.searchParams.get('code') || ''
  const state = url.searchParams.get('state') || ''

  // 手动模式下不读取地址栏 code，避免旧授权链接里的 code 被静默提交。
  if (config.wx.oauth.enable && code) {
    await exchangeCallback(code, state)
    return
  }

  clearOAuthQuery()
  // 无 token 的进入/刷新统一清掉未完成的待绑定身份。
  clearAuth()
  // 自动模式且没有可用回跳 code 时才跳转；手动模式直接返回，由登录页展示输入框。
  if (config.wx.oauth.enable) redirectToAuthorize()
}

// 用户主动点“重新授权”：清掉上次错误后重新发起，不受自动重授权次数限制。
export function retryParentLogin() {
  authState.error = ''
  redirectToAuthorize()
}

// 401 后静默重授权一次；本会话已重授权过或本就用手动模式时返回 false，由页面回退登录页。
export function startReauthorize() {
  if (!config.wx.oauth.enable) return false
  try {
    if (window.sessionStorage.getItem(RETRY_KEY)) return false
    window.sessionStorage.setItem(RETRY_KEY, '1')
  } catch {
    // 无法记录去重标记时不自动跳转，避免后端持续 401 时反复跳微信。
    return false
  }
  redirectToAuthorize()
  return true
}

// 手动模式入口：仅在用户提交输入框后兑换 code。
export function loginWithManualCode(value) {
  const code = String(value || '').trim()
  if (!code) throw new Error('请输入微信授权 code')
  return exchangeCode(code)
}
